import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@proaxiv/ui'
import { AVAILABLE_FEATURES, type FeatureConfig, type OrgFeatureMap } from '../types'
import { Button } from './Button'

// ── Icons ──────────────────────────────────────────────────────────────────────

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

interface Props {
  orgName: string
  initialFeatures: OrgFeatureMap
  onSave: (features: OrgFeatureMap) => void
  onCancel: () => void
}

/** Modal for enabling/disabling features and setting quotas for an accepted organization. */
export function FeatureEditorModal({ orgName, initialFeatures, onSave, onCancel }: Props) {
  const { t } = useTranslation()

  const [draft, setDraft] = useState<OrgFeatureMap>(() => {
    const initial: OrgFeatureMap = {}
    for (const feature of AVAILABLE_FEATURES) {
      initial[feature] = initialFeatures[feature] ?? { enabled: false, quota: 1 }
    }
    return initial
  })

  const [lastNumericQuota, setLastNumericQuota] = useState<Record<string, number>>(() => {
    const map: Record<string, number> = {}
    for (const feature of AVAILABLE_FEATURES) {
      const cfg = initialFeatures[feature]
      map[feature] = cfg && typeof cfg.quota === 'number' ? cfg.quota : 1
    }
    return map
  })

  function toggleFeature(feature: string, enabled: boolean) {
    setDraft((prev) => ({ ...prev, [feature]: { ...prev[feature], enabled } }))
  }

  function toggleInfinite(feature: string, infinite: boolean) {
    setDraft((prev) => {
      const current = prev[feature]
      return infinite
        ? { ...prev, [feature]: { ...current, quota: 'infinite' } }
        : { ...prev, [feature]: { ...current, quota: lastNumericQuota[feature] ?? 1 } }
    })
  }

  function updateQuota(feature: string, value: string) {
    const parsed = parseInt(value, 10)
    const num = isNaN(parsed) || parsed < 1 ? 1 : parsed
    setLastNumericQuota((prev) => ({ ...prev, [feature]: num }))
    setDraft((prev) => ({ ...prev, [feature]: { ...prev[feature], quota: num } }))
  }

  return (
    <Modal labelledBy="feature-editor-title">
      <div className="border-pax-purple/50 relative w-full max-w-md rounded-2xl border bg-white p-4 shadow-2xl ring-1 ring-black/5 sm:p-6">
        <button
          type="button"
          onClick={onCancel}
          className="text-pax-gray hover:text-pax-black absolute top-3 right-3 cursor-pointer rounded-lg p-1 transition-colors focus-visible:outline-none sm:top-4 sm:right-4"
          aria-label="Close"
        >
          <CloseIcon />
        </button>

        <h2
          id="feature-editor-title"
          className="text-pax-black mb-3 pr-8 text-base font-bold break-words sm:mb-5 sm:text-xl"
        >
          {t('feature_editor_title', { orgName })}
        </h2>

        <div className="mb-4 flex flex-col gap-2 sm:mb-6 sm:gap-3">
          {AVAILABLE_FEATURES.map((feature) => {
            const cfg: FeatureConfig = draft[feature] ?? { enabled: false, quota: 1 }
            const isInfinite = cfg.quota === 'infinite'
            const numericQuota =
              typeof cfg.quota === 'number' ? cfg.quota : (lastNumericQuota[feature] ?? 1)

            return (
              <div
                key={feature}
                className="border-pax-purple/30 bg-pax-light-purple rounded-xl border p-3 sm:p-4"
              >
                <label className="flex cursor-pointer items-center gap-2.5 text-sm font-medium select-none">
                  <input
                    type="checkbox"
                    aria-label={`Enable ${feature}`}
                    checked={cfg.enabled}
                    onChange={(e) => toggleFeature(feature, e.target.checked)}
                    className="accent-pax-blue h-4 w-4 cursor-pointer rounded"
                  />
                  <span className="text-pax-black">{feature}</span>
                </label>

                {cfg.enabled && (
                  <div className="mt-3 flex items-center gap-3">
                    <label className="text-pax-gray text-xs">{t('quota_label')}</label>
                    <input
                      type="number"
                      aria-label={`${feature} quota`}
                      min={1}
                      value={numericQuota}
                      disabled={isInfinite}
                      onChange={(e) => updateQuota(feature, e.target.value)}
                      className="border-pax-purple/40 text-pax-black focus:border-pax-blue disabled:text-pax-gray w-20 rounded-lg border bg-white px-2 py-1 text-sm transition-colors focus:outline-none"
                    />
                    <label className="text-pax-black flex cursor-pointer items-center gap-1.5 text-xs select-none">
                      <input
                        type="checkbox"
                        aria-label="Infinite quota"
                        checked={isInfinite}
                        onChange={(e) => toggleInfinite(feature, e.target.checked)}
                        className="accent-pax-blue h-4 w-4 cursor-pointer rounded"
                      />
                      {t('infinite_label')}
                    </label>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
          <Button variant="secondary" onClick={onCancel} className="w-full sm:w-auto">
            {t('btn_cancel')}
          </Button>
          <Button variant="primary" onClick={() => onSave(draft)} className="w-full sm:w-auto">
            {t('btn_confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
